import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  Button,
  SimpleGrid,
  Badge,
  Divider,
  Spinner,
} from "@chakra-ui/react";
import Header from "./Header";
import { MAIN, MAIN_BACKGROUND } from "./colours";
import { FEATURED_EVENT_ID, EVENTS } from "./events/eventConfig";
import { fetchGuestList, formatGBP } from "./booking/api";

const STORAGE_KEY = "hf_admin_key";

function GuestCard({ booking }) {
  return (
    <Box border={`1px solid ${MAIN}`} p="4">
      <Flex justify="space-between" wrap="wrap" gap="2">
        <Text fontWeight="bold">
          {booking.ref} · {booking.lead.firstName} {booking.lead.lastName}
        </Text>
        <Text>
          {booking.guestCount} {booking.guestCount === 1 ? "guest" : "guests"} ·{" "}
          {formatGBP(booking.amountTotal)}
        </Text>
      </Flex>
      <Text fontSize="sm" opacity={0.8} mb="3">
        {booking.lead.email} · {booking.lead.phone}
      </Text>
      <Divider borderColor={MAIN} opacity={0.4} mb="3" />
      {booking.guests.map((g, i) => (
        <Box key={i} mb="2">
          <Text fontWeight="bold" fontSize="sm">
            {i + 1}. {g.name}
          </Text>
          {(g.dietary || g.allergies || g.notes) && (
            <Text fontSize="sm">
              {g.dietary && <>Dietary: {g.dietary}. </>}
              {g.allergies && (
                <Text as="span" color="#ffd7d5">
                  Allergies: {g.allergies}.{" "}
                </Text>
              )}
              {g.notes && <>Notes: {g.notes}.</>}
            </Text>
          )}
        </Box>
      ))}
      {booking.seatingRequests && (
        <Text fontSize="sm" mt="2">
          <strong>Seating:</strong> {booking.seatingRequests}
        </Text>
      )}
    </Box>
  );
}

export default function Admin() {
  const [key, setKey] = useState("");
  const [eventId, setEventId] = useState(FEATURED_EVENT_ID);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authed, setAuthed] = useState(false);

  const load = useCallback(
    async (adminKey, evId) => {
      setLoading(true);
      setError("");
      try {
        const result = await fetchGuestList(evId, adminKey);
        setData(result);
        setAuthed(true);
        localStorage.setItem(STORAGE_KEY, adminKey);
      } catch (err) {
        setError(err.message || "Could not load bookings.");
        if (err.status === 401) {
          setAuthed(false);
          localStorage.removeItem(STORAGE_KEY);
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Try a stored key on first load.
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setKey(stored);
      load(stored, FEATURED_EVENT_ID);
    }
  }, [load]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (key.trim()) load(key.trim(), eventId);
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setData(null);
    setKey("");
  };

  return (
    <>
      <Header />
      <Flex bgColor={MAIN_BACKGROUND} justify="center" py={{ base: "12", md: "16" }} minH="70vh">
        <Box px={{ base: "6", md: "10" }} maxW="900px" w="100%" color={MAIN}>
          <Flex justify="space-between" align="center" wrap="wrap" gap="3" mb="8">
            <Heading size="xl">Guest list</Heading>
            {authed && (
              <Button
                size="sm"
                variant="outline"
                color={MAIN}
                borderColor={MAIN}
                borderRadius="none"
                _hover={{ bg: MAIN, color: MAIN_BACKGROUND }}
                onClick={logout}
              >
                Log out
              </Button>
            )}
          </Flex>

          {!authed && (
            <Box as="form" onSubmit={handleLogin} maxW="360px">
              <Text mb="2" fontSize="sm">
                Enter the admin password to view bookings.
              </Text>
              <Input
                type="password"
                bg="white"
                color={MAIN_BACKGROUND}
                borderRadius="none"
                focusBorderColor={MAIN}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                mb="3"
                placeholder="Admin password"
              />
              {/* Event selector — handy once there are multiple events. */}
              {Object.keys(EVENTS).length > 1 && (
                <Box mb="3">
                  {Object.values(EVENTS).map((ev) => (
                    <Button
                      key={ev.id}
                      size="sm"
                      mr="2"
                      mb="2"
                      borderRadius="none"
                      bg={eventId === ev.id ? MAIN : "transparent"}
                      color={eventId === ev.id ? MAIN_BACKGROUND : MAIN}
                      border={`1px solid ${MAIN}`}
                      onClick={() => setEventId(ev.id)}
                    >
                      {ev.title}
                    </Button>
                  ))}
                </Box>
              )}
              <Button
                type="submit"
                bg={MAIN}
                color={MAIN_BACKGROUND}
                borderRadius="none"
                isLoading={loading}
                _hover={{ bg: "transparent", color: MAIN, border: `2px solid ${MAIN}` }}
              >
                View bookings
              </Button>
              {error && (
                <Text color="#ffd7d5" mt="4" fontSize="sm">
                  {error}
                </Text>
              )}
            </Box>
          )}

          {authed && loading && (
            <Flex align="center" gap="3">
              <Spinner color={MAIN} /> <Text>Loading bookings…</Text>
            </Flex>
          )}

          {authed && !loading && data && (
            <Box>
              <Text fontSize="lg" mb="1">
                {data.event.name} — {data.event.dateLabel}
              </Text>
              <Flex gap="6" wrap="wrap" mb="6" fontSize="sm" opacity={0.9}>
                <Text>
                  <strong>{data.totals.bookings}</strong> bookings
                </Text>
                <Text>
                  <strong>{data.totals.guests}</strong> guests
                </Text>
                <Text>
                  <strong>{formatGBP(data.totals.revenue)}</strong> taken
                </Text>
              </Flex>

              <Flex justify="flex-end" mb="4">
                <Button
                  size="sm"
                  variant="outline"
                  color={MAIN}
                  borderColor={MAIN}
                  borderRadius="none"
                  _hover={{ bg: MAIN, color: MAIN_BACKGROUND }}
                  onClick={() => load(key, eventId)}
                >
                  Refresh
                </Button>
              </Flex>

              {Object.values(data.sittings).map((sitting) => (
                <Box key={sitting.id} mb="10">
                  <Flex align="center" gap="3" mb="4" wrap="wrap">
                    <Heading size="md">{sitting.label}</Heading>
                    <Badge bg={MAIN} color={MAIN_BACKGROUND} borderRadius="none">
                      {sitting.guestsBooked}/{sitting.capacity} booked
                    </Badge>
                    {sitting.remaining === 0 && (
                      <Badge bg="#a94442" color="white" borderRadius="none">
                        Sold out
                      </Badge>
                    )}
                  </Flex>
                  {sitting.bookings.length === 0 ? (
                    <Text opacity={0.7} fontSize="sm">
                      No bookings yet.
                    </Text>
                  ) : (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                      {sitting.bookings.map((b) => (
                        <GuestCard key={b.ref} booking={b} />
                      ))}
                    </SimpleGrid>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Flex>
    </>
  );
}
